import { rewriteLegacyOrderSummaryCols, setHideAmountDue } from './orderSummary';

export const DEFAULT_BRAND_COLORS = {
  header: '#00a7e1',
  button: '#00a7e1',
  text: '#0f172a',
  card: '#ffffff',
};

const HEX_RE = /^#([0-9a-fA-F]{6})$/;

export const normalizeHex = (value, fallback) => {
  const raw = String(value || '').trim();
  if (HEX_RE.test(raw)) {
    return raw.toLowerCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    return `#${raw.toLowerCase()}`;
  }
  return fallback;
};

export const parseBrandColors = (html) => {
  const source = String(html || '');
  const pick = (name, fallback) => {
    const match = source.match(
      new RegExp(`data-${name}="(#[0-9A-Fa-f]{6})"`, 'i')
    );
    return normalizeHex(match && match[1], fallback);
  };
  return {
    header: pick('header', DEFAULT_BRAND_COLORS.header),
    button: pick('button', DEFAULT_BRAND_COLORS.button),
    text: pick('text', DEFAULT_BRAND_COLORS.text),
    card: pick('card-fill', DEFAULT_BRAND_COLORS.card),
  };
};

const themeNode = (colors) =>
  `<div data-mcc-theme="1" data-header="${colors.header}" data-button="${colors.button}" data-text="${colors.text}" data-card-fill="${colors.card}" style="display:none;">&nbsp;</div>`;

const upsertBrandTheme = (html, colors) => {
  const node = themeNode(colors);
  if (/data-mcc-theme="1"/.test(html || '')) {
    return String(html).replace(
      /<div[^>]*data-mcc-theme="1"[^>]*>[\s\S]*?<\/div>/i,
      node
    );
  }
  return `${node}${html || ''}`;
};

const setAttrStyle = (html, attrNeedle, style) => {
  const source = String(html || '');
  const re = new RegExp(
    `<([a-z0-9]+)([^>]*\\s${attrNeedle}[^>]*)>`,
    'gi'
  );
  return source.replace(re, (full, tag, rest) => {
    if (/\sstyle="/i.test(rest)) {
      return `<${tag}${rest.replace(/\sstyle="[^"]*"/i, ` style="${style}"`)}>`;
    }
    return `<${tag}${rest} style="${style}">`;
  });
};

const setBrandStyle = (html, brand, style) =>
  setAttrStyle(html, `data-brand="${brand}"`, style);

const BODY_COPY_COLORS = ['#334155', '#0f172a'];

const hexToRgbNumbers = (hex) => {
  const body = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(body)) {
    return null;
  }
  const value = parseInt(body, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const replaceColorInStyle = (style, fromHex, toHex) => {
  const hexBody = String(fromHex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hexBody)) {
    return style;
  }
  let next = String(style || '').replace(
    new RegExp(`(^|;)(\\s*)color\\s*:\\s*#${hexBody}\\b`, 'gi'),
    `$1$2color:${toHex}`
  );
  const rgb = hexToRgbNumbers(fromHex);
  if (rgb) {
    next = next.replace(
      new RegExp(
        `(^|;)(\\s*)color\\s*:\\s*rgb\\(\\s*${rgb[0]}\\s*,\\s*${rgb[1]}\\s*,\\s*${rgb[2]}\\s*\\)`,
        'gi'
      ),
      `$1$2color:${toHex}`
    );
  }
  return next;
};

const replaceStyleColors = (html, sources, color) =>
  String(html || '').replace(
    /style=(["'])([\s\S]*?)\1/gi,
    (full, quote, style) => {
      let next = style;
      sources.forEach((source) => {
        next = replaceColorInStyle(next, source, color);
      });
      return `style=${quote}${next}${quote}`;
    }
  );

const paintStyleBackground = (style, color) => {
  let next = String(style || '');
  let painted = false;
  if (/background-color\s*:/i.test(next)) {
    next = next.replace(
      /background-color\s*:\s*[^;"]+/i,
      `background-color:${color}`
    );
    painted = true;
  }
  if (/(^|;)\s*background\s*:/i.test(next)) {
    next = next.replace(
      /(^|;)\s*background\s*:\s*[^;"]+/i,
      `$1background:${color}`
    );
    painted = true;
  }
  if (!painted) {
    next = `background-color:${color};${next}`;
  }
  return next;
};

const paintTagBackground = (tag, color) => {
  let next = tag;
  if (/bgcolor\s*=\s*["'][^"']*["']/i.test(next)) {
    next = next.replace(
      /bgcolor\s*=\s*["'][^"']*["']/i,
      `bgcolor="${color}"`
    );
  }
  if (/style\s*=\s*"/i.test(next)) {
    next = next.replace(
      /style\s*=\s*"([^"]*)"/i,
      (match, style) => `style="${paintStyleBackground(style, color)}"`
    );
  } else if (/style\s*=\s*'([^']*)'/i.test(next)) {
    next = next.replace(
      /style\s*=\s*'([^']*)'/i,
      (match, style) => `style='${paintStyleBackground(style, color)}'`
    );
  } else {
    next = next.replace(/>$/, ` style="background-color:${color};">`);
  }
  return next;
};

const paintMatchingTags = (html, tagRe, color) =>
  String(html || '').replace(tagRe, (tag) => paintTagBackground(tag, color));

// Modern email canvases paint the body on table.wrap and on content cards
// (details, pickup code, summary, hours, columns). Those nodes do not carry
// data-brand="card", so the card-fill swatch never reached the preview.
export const applyCardFillToHtml = (html, cardColor) => {
  const color = normalizeHex(cardColor, DEFAULT_BRAND_COLORS.card);
  let out = String(html || '');
  out = paintMatchingTags(
    out,
    /<table\b[^>]*\bclass=(["'])[^"']*\bwrap\b[^"']*\1[^>]*>/gi,
    color
  );
  out = paintMatchingTags(
    out,
    /<table\b[^>]*\bdata-card=(["'])(?:details|pickup_qr|summary)\1[^>]*>/gi,
    color
  );
  out = paintMatchingTags(
    out,
    /<div\b[^>]*\bstyle=(["'])[^"']*border:\s*1px solid #e2e8f0[^"']*\1[^>]*>/gi,
    color
  );
  out = paintMatchingTags(
    out,
    /<td\b[^>]*\bstyle=(["'])[^"']*border:\s*1px dashed #cbd5e1[^"']*\1[^>]*>/gi,
    color
  );
  return out;
};

// Skeleton copy is color:#334155. The Body text swatch only restyled
// data-brand="text" nodes, so the greeting and message stayed dark.
export const applyBodyTextToHtml = (html, textColor, previousText) => {
  const color = normalizeHex(textColor, DEFAULT_BRAND_COLORS.text);
  const sources = [];
  const push = (value) => {
    const hex = normalizeHex(value, '');
    if (!hex || hex === color || hex === '#ffffff' || hex === '#fff') {
      return;
    }
    if (sources.indexOf(hex) === -1) {
      sources.push(hex);
    }
  };
  BODY_COPY_COLORS.forEach(push);
  push(previousText);
  let out = replaceStyleColors(html, sources, color);
  const re = /<([a-z0-9]+)([^>]*\sdata-brand="text"[^>]*)>/gi;
  out = out.replace(re, (full, tag, rest) => {
    if (/\sstyle="/i.test(rest)) {
      return `<${tag}${rest.replace(/\sstyle="([^"]*)"/i, (match, style) => {
        const next = /(^|;)\s*color\s*:/i.test(style)
          ? style.replace(/(^|;)\s*color\s*:\s*[^;"]+/i, `$1color:${color}`)
          : `${style}${style && !/;\s*$/.test(style) ? ';' : ''}color:${color}`;
        return ` style="${next}"`;
      })}>`;
    }
    return `<${tag}${rest} style="color:${color};">`;
  });
  return out;
};

export const applyBrandColorsToHtml = (html, colors) => {
  const previousText = parseBrandColors(html).text;
  let out = upsertBrandTheme(html, colors);
  const headerStyle = `background:${colors.header};color:#ffffff;padding:28px 24px;width:100%;`;
  out = setBrandStyle(out, 'header', headerStyle);
  out = setAttrStyle(out, 'data-card="header"', headerStyle);
  out = setBrandStyle(
    out,
    'cta',
    `padding:20px 24px;background:${colors.header};color:#ffffff;`
  );
  out = setBrandStyle(
    out,
    'button',
    `display:inline-block;background:${colors.button};color:#ffffff;padding:10px 16px;border-radius:8px;font-weight:700;text-decoration:none;`
  );
  out = setBrandStyle(
    out,
    'card',
    `background:${colors.card};color:${colors.text};border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;margin:0 0 12px;`
  );
  out = applyBodyTextToHtml(out, colors.text, previousText);
  out = applyCardFillToHtml(out, colors.card);
  return out;
};

const tokenChip = (name) =>
  `<span class="mceNonEditable" data-locked="true" data-token="${name}">{{${name}}}</span>`;

const escapeAttr = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');

export const isUsableBrandLogoUrl = (value) => {
  const url = String(value || '').trim();
  return (
    !!url &&
    /^https?:\/\//i.test(url) &&
    !/NoImageFound/i.test(url)
  );
};

export const DEFAULT_ACCOUNT_LOGO_URL =
  'https://rpx-dev-ext-images-qr.s3.amazonaws.com/ilocal-logo-primary.png';

export const resolveAccountLogoUrl = (value) =>
  isUsableBrandLogoUrl(value) ? String(value).trim() : DEFAULT_ACCOUNT_LOGO_URL;

export const ACCOUNT_LOGO_TOKEN_HTML =
  '<span class="mceNonEditable" data-locked="true" data-token="Account Logo" data-logo="account">{{Account Logo}}</span>';

export const accountLogoImgHtml = (logoUrl) => {
  const src = resolveAccountLogoUrl(logoUrl);
  return `<img class="mceNonEditable" data-locked="true" data-token="Account Logo" data-logo="account" src="${escapeAttr(
    src
  )}" alt="iLocalBox" height="48" style="max-height:48px;max-width:200px;width:auto;border:0;outline:none;display:block;margin:0 auto;background:#ffffff;border-radius:6px;padding:6px 10px;" />`;
};

export const serializeAccountLogo = (html) =>
  String(html || '').replace(
    /<img[^>]*data-token=["']Account Logo["'][^>]*\/?>/gi,
    ACCOUNT_LOGO_TOKEN_HTML
  );

export const injectAccountLogo = (html, logoUrl) => {
  const source = String(html || '');
  if (!source.trim()) {
    return source;
  }
  const img = accountLogoImgHtml(logoUrl);
  let next = source
    .replace(/<img[^>]*data-token=["']Account Logo["'][^>]*\/?>/gi, img)
    .replace(
      /<(span|p)[^>]*data-token=["']Account Logo["'][^>]*>[\s\S]*?<\/\1>/gi,
      img
    )
    .replace(/\{\{\s*Account Logo\s*\}\}/gi, img);
  next = next.replace(
    /(<(?:td|div)[^>]*data-(?:card|brand)=["']header["'][^>]*>)([\s\S]*?)(<\/(?:td|div)>)/i,
    (full, open, inner, close) => {
      if (
        !/data-token=["']Account Name["']/i.test(inner) &&
        !/\{\{\s*Account Name\s*\}\}/i.test(inner)
      ) {
        return full;
      }
      const swapped = inner
        .replace(
          /<(span|p)[^>]*data-token=["']Account Name["'][^>]*>[\s\S]*?<\/\1>/gi,
          img
        )
        .replace(/\{\{\s*Account Name\s*\}\}/gi, img);
      return `${open}${swapped}${close}`;
    }
  );
  return next;
};

export const EMAIL_ICON_BASE =
  'https://s3.amazonaws.com/rpx-stage-ext-images-qr/email-icons';

const replaceFirstTableWithAttr = (html, attrNeedle, replacement) => {
  const source = String(html || '');
  const re = new RegExp(`<table\\b[^>]*${attrNeedle}[^>]*>`, 'i');
  const match = re.exec(source);
  if (!match) {
    return source;
  }
  const start = match.index;
  const lower = source.toLowerCase();
  let depth = 0;
  let i = start;
  while (i < source.length) {
    const openAt = lower.indexOf('<table', i);
    const closeAt = lower.indexOf('</table>', i);
    if (closeAt === -1) {
      break;
    }
    if (openAt !== -1 && openAt < closeAt) {
      depth += 1;
      i = openAt + 6;
    } else {
      depth -= 1;
      i = closeAt + 8;
      if (depth === 0) {
        return source.slice(0, start) + replacement + source.slice(i);
      }
    }
  }
  return source;
};

export const rewriteCodeExpiresInDays = (html) =>
  String(html || '')
    .replace(/Code expires on/gi, 'Code expires in')
    .replace(/El c[oó]digo vence el/gi, 'El código vence en')
    .replace(/Le code expire le/gi, 'Le code expire dans');

const AMOUNT_TOKEN_RE =
  /<span\b[^>]*data-token=["']Amount["'][^>]*>[\s\S]*?<\/span>|\{\{\s*Amount\s*\}\}/i;

export const totalDueTableHtml = (amountInner) =>
  `<table data-mcc-total-due="1" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;max-width:100%;border-collapse:collapse;">
<tbody>
<tr>
<td valign="middle" align="left" style="font-family:Helvetica, Arial, sans-serif;font-size:14px;line-height:21px;color:#334155;padding:0;text-align:left;">Total Due</td>
<td valign="middle" align="right" style="font-family:Helvetica, Arial, sans-serif;font-size:20px;line-height:24px;font-weight:700;text-align:right;color:#1e3a5f;white-space:nowrap;padding:0 0 0 16px;">${amountInner}</td>
</tr>
</tbody>
</table>`;

export const payNowAnchorHtml = (colors) => {
  const c = colors || DEFAULT_BRAND_COLORS;
  return `<a class="mceNonEditable" data-locked="true" data-mcc-pay-now="1" data-brand="button" href="{{Link}}" data-href="{{Link}}" target="_blank" style="display:inline-block;background:${c.button};color:#ffffff;padding:12px 28px;border-radius:6px;font-weight:700;text-decoration:none;font-size:15px;font-family:Helvetica, Arial, sans-serif;margin:0;">Pay Now</a>`;
};

export const payNowBlockHtml = (colors) =>
  `<div data-mcc-pay-now-wrap="1" data-mcc-block="1" align="center" style="text-align:center;padding:16px 0 0;width:100%;">
<p data-mcc-pay-note="1" style="font-family:Helvetica, Arial, sans-serif;font-size:12px;line-height:18px;color:#64748b;margin:0 0 12px;text-align:center;">Save time and pay online before you arrive.</p>
${payNowAnchorHtml(colors)}
</div>`;

const stripLooseLinkChips = (html) =>
  String(html || '')
    .replace(/<span\b[^>]*data-mcc-link=["']1["'][^>]*>[\s\S]*?<\/span>/gi, '')
    .replace(/<span\b[^>]*data-token=["']Link["'][^>]*>[\s\S]*?<\/span>/gi, '')
    .replace(/<p\b[^>]*>\s*(?:\{\{\s*Link\s*\}\}|ilocal\.link\/[^\s<]+)\s*<\/p>/gi, '');

const isInsideTotalDueTable = (source, offset) => {
  const before = source.slice(Math.max(0, offset - 800), offset);
  const mark = before.toLowerCase().lastIndexOf('data-mcc-total-due');
  if (mark === -1) {
    return false;
  }
  return before.slice(mark).toLowerCase().indexOf('</table>') === -1;
};

const hasPayNowMarkup = (html) =>
  /data-mcc-pay-now/i.test(html || '') ||
  /<a\b[^>]*>\s*Pay Now\s*<\/a>/i.test(html || '') ||
  /<(p|div)\b[^>]*data-brand=["']button["'][^>]*>[\s\S]*?Pay Now[\s\S]*?<\/\1>/i.test(
    html || ''
  );

const stripPayNowMarkup = (html) =>
  String(html || '')
    .replace(
      /<div\b[^>]*data-mcc-pay-now-wrap[^>]*>[\s\S]*?<\/div>/gi,
      ''
    )
    .replace(/<p\b[^>]*data-mcc-pay-note[^>]*>[\s\S]*?<\/p>/gi, '')
    .replace(
      /<p\b[^>]*>\s*Save time and pay online before you arrive\.?\s*<\/p>/gi,
      ''
    )
    .replace(/<a\b[^>]*data-mcc-pay-now[^>]*>[\s\S]*?<\/a>/gi, '')
    .replace(/<a\b[^>]*>\s*Pay Now\s*<\/a>/gi, '')
    .replace(
      /<(p|div)\b[^>]*data-brand=["']button["'][^>]*>[\s\S]*?Pay Now[\s\S]*?<\/\1>/gi,
      ''
    );

const stripPaymentLayoutCards = (html) =>
  String(html || '')
    .replace(/<div\b[^>]*data-card=["']payment["'][^>]*>[\s\S]*?<\/div>/gi, '')
    .replace(
      /<table\b[^>]*data-card=["']payment["'][^>]*>[\s\S]*?<\/table>/gi,
      ''
    );

const stripDuplicatePaymentSummaries = (html) =>
  String(html || '').replace(
    /<(table|div)\b[^>]*data-card=["']payment["'][^>]*>[\s\S]*?<\/\1>/gi,
    (full) => (/Payment Summary/i.test(full) ? '' : full)
  );

const hasWellFormedTotalDue = (html) => {
  const block = String(html || '').match(
    /<table\b[^>]*data-mcc-total-due[^>]*>[\s\S]*?<\/table>/i
  );
  if (!block) {
    return false;
  }
  const cells = block[0].match(/<td\b/gi) || [];
  return cells.length >= 2 && /Total Due/i.test(block[0]);
};

const replaceSmashedTotalDue = (html, rebuilt) => {
  let source = String(html || '');
  if (/data-mcc-total-due/i.test(source)) {
    return source.replace(
      /<table\b[^>]*data-mcc-total-due[^>]*>[\s\S]*?<\/table>/gi,
      rebuilt
    );
  }
  const block = /<(p|div)\b[^>]*>\s*(?:<[^>]+>\s*)*Total Due[\s\S]*?(?:<span\b[^>]*data-token=["']Amount["'][^>]*>[\s\S]*?<\/span>|\{\{\s*Amount\s*\}\}|\$\s*[0-9][0-9,]*(?:\.[0-9]{2})?)[\s\S]*?<\/\1>/i;
  if (block.test(source)) {
    return source.replace(block, rebuilt);
  }
  return source.replace(
    /Total Due[\s\u00a0]*(?:<(?:span|font)[^>]*>[\s\u00a0]*)*(?:<span\b[^>]*data-token=["']Amount["'][^>]*>[\s\S]*?<\/span>|\{\{\s*Amount\s*\}\}|\$\s*[0-9][0-9,]*(?:\.[0-9]{2})?)/i,
    rebuilt
  );
};

const insertAfterTotalDue = (html, block) => {
  const source = String(html || '');
  const due = /<table\b[^>]*data-mcc-total-due[^>]*>[\s\S]*?<\/table>/i.exec(
    source
  );
  if (due) {
    const at = due.index + due[0].length;
    return `${source.slice(0, at)}${block}${source.slice(at)}`;
  }
  const heading = /Payment Summary/i.exec(source);
  if (heading) {
    const at = heading.index + heading[0].length;
    return `${source.slice(0, at)}${block}${source.slice(at)}`;
  }
  return `${source}${block}`;
};

export const rewritePayNowButton = (html, colors) => {
  let source = stripLooseLinkChips(String(html || ''));
  if (!hasPayNowMarkup(source)) {
    return source;
  }
  source = stripPayNowMarkup(source);
  return insertAfterTotalDue(source, payNowBlockHtml(colors));
};

export const insertPayNowBelowPayment = (html, colors) => {
  let source = stripPaymentLayoutCards(String(html || ''));
  source = stripLooseLinkChips(source);
  source = stripPayNowMarkup(source);
  return insertAfterTotalDue(source, payNowBlockHtml(colors));
};

export const foldPayNowIntoSummary = (html, colors) => {
  const original = String(html || '');
  const hadExtraCard = /data-card=["']payment["']/i.test(original);
  const hadPayNow = hasPayNowMarkup(original);
  let source = stripPaymentLayoutCards(original);
  source = stripLooseLinkChips(source);
  if (!hadExtraCard && !hadPayNow) {
    return source;
  }
  source = stripPayNowMarkup(source);
  return insertAfterTotalDue(source, payNowBlockHtml(colors));
};

export const rewriteTotalDueRow = (html, colors) => {
  const original = String(html || '');
  const wantPayNow =
    /data-card=["']payment["']/i.test(original) || hasPayNowMarkup(original);
  let source = stripDuplicatePaymentSummaries(original);
  source = stripLooseLinkChips(source);
  const tokenMatch = source.match(AMOUNT_TOKEN_RE);
  const amountInner = tokenMatch ? tokenMatch[0] : tokenChip('Amount');
  const rebuilt = totalDueTableHtml(amountInner);
  if (!hasWellFormedTotalDue(source)) {
    source = replaceSmashedTotalDue(source, rebuilt);
    source = source.replace(
      /<(p|div)\b[^>]*>\s*Total Due\s*(?:<span\b[^>]*data-token=["']Amount["'][^>]*>[\s\S]*?<\/span>|\{\{\s*Amount\s*\}\})\s*<\/\1>/gi,
      rebuilt
    );
    source = source.replace(
      /Total Due\s*(?:<span\b[^>]*data-token=["']Amount["'][^>]*>[\s\S]*?<\/span>|\{\{\s*Amount\s*\}\})/gi,
      (full, offset) => (isInsideTotalDueTable(source, offset) ? full : rebuilt)
    );
  }
  if (wantPayNow && !hasPayNowMarkup(source)) {
    source = insertAfterTotalDue(source, payNowBlockHtml(colors));
  } else if (wantPayNow && !/data-mcc-pay-now/i.test(source)) {
    source = stripPayNowMarkup(source);
    source = insertAfterTotalDue(source, payNowBlockHtml(colors));
  }
  return source;
};

export const rewritePickupCodeHighlight = (html) => {
  const source = String(html || '');
  const re = /<p\b[^>]*>\s*(?:<span\b[^>]*data-token=["']Pickup Code["'][^>]*>[\s\S]*?<\/span>|\{\{\s*Pickup Code\s*\}\})\s*<\/p>/gi;
  return source.replace(re, (full, offset) => {
    const before = source.slice(Math.max(0, offset - 320), offset);
    if (/data-pickup-code-chip/i.test(before)) {
      return full;
    }
    const token = full.match(
      /<span\b[^>]*data-token=["']Pickup Code["'][^>]*>[\s\S]*?<\/span>|\{\{\s*Pickup Code\s*\}\}/i
    );
    const inner = token ? token[0] : '{{Pickup Code}}';
    return `<div data-pickup-code-chip="1" style="background-color:#F0F2F5;border-radius:8px;padding:12px 8px;margin:0 0 12px;">
<p style="margin:0;font-size:30px;line-height:34px;font-weight:700;letter-spacing:6px;color:#1e3a5f;font-family:Helvetica, Arial, sans-serif;text-align:center;">${inner}</p>
</div>`;
  });
};

export const normalizeEmailTableLayout = (html) => {
  let out = String(html || '')
    .replace(/<colgroup\b[^>]*>[\s\S]*?<\/colgroup>/gi, '')
    .replace(/<col\b[^>]*\/?>/gi, '')
    .replace(/\sdata-mce-width=["'][^"']*["']/gi, '');
  const nested = /data-card=["']hours_grid["']/i.test(out);
  const hoursCount = (out.match(/data-card=["']hours["']/gi) || []).length;
  if (nested || hoursCount > 1) {
    const pair = buildCardHtml('hours_grid');
    out = removeAllTablesWithAttr(out, 'data-card=["\']hours_grid["\']');
    if (/data-card=["']hours["']/i.test(out)) {
      out = replaceFirstTableWithAttr(
        out,
        'data-card=["\']hours["\']',
        '<!--MCC_HOURS-->'
      );
      out = removeAllTablesWithAttr(out, 'data-card=["\']hours["\']');
      out = out.replace('<!--MCC_HOURS-->', pair);
    } else if (/data-card=["']badges["']/i.test(out)) {
      out = out.replace(
        /<table\b[^>]*data-card=["']badges["']/i,
        `${pair}$&`
      );
    } else {
      out = `${out}${pair}`;
    }
  }
  return rewriteTotalDueRow(
    rewriteCodeExpiresInDays(
      rewritePickupCodeHighlight(rewriteLegacyOrderSummaryCols(out))
    )
  );
};

const removeAllTablesWithAttr = (html, attrNeedle) => {
  let out = String(html || '');
  let guard = 0;
  while (guard < 8 && new RegExp(attrNeedle, 'i').test(out)) {
    const next = replaceFirstTableWithAttr(out, attrNeedle, '');
    if (next === out) {
      break;
    }
    out = next;
    guard += 1;
  }
  return out;
};

const matchingRange = (html, startIndex, tag) => {
  const source = String(html || '');
  const lower = source.toLowerCase();
  const openNeedle = `<${tag}`;
  const closeNeedle = `</${tag}>`;
  let depth = 0;
  let i = startIndex;
  while (i < source.length) {
    const openAt = lower.indexOf(openNeedle, i);
    const closeAt = lower.indexOf(closeNeedle, i);
    if (closeAt === -1) {
      return null;
    }
    const openChar = openAt === -1 ? '' : lower[openAt + openNeedle.length] || '';
    const openValid =
      openAt !== -1 &&
      openAt < closeAt &&
      (openChar === '>' || openChar === ' ' || openChar === '\n' || openChar === '\t');
    if (openValid) {
      depth += 1;
      i = openAt + openNeedle.length;
    } else {
      depth -= 1;
      i = closeAt + closeNeedle.length;
      if (depth === 0) {
        return { start: startIndex, end: i };
      }
    }
  }
  return null;
};

const extractFirstMatch = (html, startRe, tag) => {
  const source = String(html || '');
  const match = startRe.exec(source);
  if (!match) {
    return null;
  }
  const range = matchingRange(source, match.index, tag);
  if (!range) {
    return null;
  }
  return {
    start: range.start,
    end: range.end,
    html: source.slice(range.start, range.end),
  };
};

const findWrapRange = (html) => {
  const source = String(html || '');
  const match = /<table\b[^>]*class="[^"]*\bwrap\b[^"]*"[^>]*>/i.exec(source);
  if (!match) {
    return null;
  }
  return matchingRange(source, match.index, 'table');
};

const footerWrapRowHtml = (inner) =>
  `<tr><td align="left" style="padding:0;width:720px;">${inner}</td></tr>`;

const FOOTER_STACK_CARDS = ['thankyou', 'footer', 'branding'];

const unwrapOutroInner = (html) => {
  const source = String(html || '').trim();
  const match = source.match(
    /^<div\b[^>]*(?:class="[^"]*mcc-email-outro|data-token=["']Email Footer["'])[^>]*>([\s\S]*)<\/div>$/i
  );
  return match ? match[1] : source;
};

const extractCardBlock = (html, cardId) => {
  const table = extractFirstMatch(
    html,
    new RegExp(`<table\\b[^>]*data-card=["']${cardId}["'][^>]*>`, 'i'),
    'table'
  );
  if (table) {
    return table;
  }
  return extractFirstMatch(
    html,
    new RegExp(`<div\\b[^>]*data-card=["']${cardId}["'][^>]*>`, 'i'),
    'div'
  );
};

const pullBlock = (bag, html, cardId) => {
  const piece = extractCardBlock(html, cardId);
  if (!piece) {
    return html;
  }
  bag[cardId] = piece.html;
  return html.slice(0, piece.start) + html.slice(piece.end);
};

const stripEmptyWrapRows = (html) =>
  String(html || '').replace(
    /<tr[^>]*>\s*<td[^>]*>\s*<\/td>\s*<\/tr>/gi,
    ''
  );

const stripFooterNote = (html) => {
  let source = String(html || '');
  let piece = extractCardBlock(source, 'footer_note');
  while (piece) {
    source = source.slice(0, piece.start) + source.slice(piece.end);
    piece = extractCardBlock(source, 'footer_note');
  }
  return source.replace(
    /<div[^>]*data-region=["']footer_note["'][^>]*>[\s\S]*?<\/div>/gi,
    ''
  );
};

const findCanvasRange = (html) => {
  const source = String(html || '');
  const match = /<table\b[^>]*data-mcc-canvas[^>]*>/i.exec(source);
  return match ? matchingRange(source, match.index, 'table') : null;
};

const pullStrayBlocks = (html) => {
  let source = String(html || '');
  const strays = [];
  for (let guard = 0; guard < 24; guard += 1) {
    const host = findCanvasRange(source) || findWrapRange(source);
    if (!host) {
      break;
    }
    const re = /<(table|div)\b[^>]*data-card=["'][^"']+["'][^>]*>/gi;
    re.lastIndex = host.end;
    const match = re.exec(source);
    const range = match && matchingRange(source, match.index, match[1].toLowerCase());
    if (!range) {
      break;
    }
    strays.push(source.slice(range.start, range.end));
    source = source.slice(0, range.start) + source.slice(range.end);
  }
  return { source, strays };
};

const restyleCardBlocks = (html, cardId, restyle) => {
  const source = String(html || '');
  const re = new RegExp(`<table\\b[^>]*data-card=["']${cardId}["'][^>]*>`, 'gi');
  let out = '';
  let cursor = 0;
  let match = re.exec(source);
  while (match) {
    const range = matchingRange(source, match.index, 'table');
    if (!range) {
      break;
    }
    out += source.slice(cursor, range.start) + restyle(source.slice(range.start, range.end));
    cursor = range.end;
    re.lastIndex = range.end;
    match = re.exec(source);
  }
  return out + source.slice(cursor);
};

const stripEmptyStackCells = (html) =>
  String(html || '').replace(/<td\b[^>]*class=["']stack["'][^>]*>\s*<\/td>/gi, '');

export const nestFooterInWrap = (html) => {
  const pulled = pullStrayBlocks(
    stripEmptyStackCells(
      rewriteTotalDueRow(rewriteCodeExpiresInDays(stripFooterNote(html)))
    )
  );
  let source = pulled.source;
  const wrap = findWrapRange(source);
  if (!wrap) {
    return source;
  }
  if (pulled.strays.length) {
    source =
      source.slice(0, wrap.end - 8) +
      pulled.strays
        .map((block) => `<tr><td align="left" style="padding:12px 24px 24px;">${block}</td></tr>`)
        .join('') +
      source.slice(wrap.end - 8);
  }
  source = restyleCardBlocks(source, 'badges', restyleBadges);
  const bag = {};
  const outro = extractFirstMatch(
    source,
    /<div\b[^>]*(?:class="[^"]*mcc-email-outro|data-token=["']Email Footer["'])[^>]*>/i,
    'div'
  );
  let leftoverOutro = '';
  if (outro) {
    leftoverOutro = unwrapOutroInner(outro.html);
    source = source.slice(0, outro.start) + source.slice(outro.end);
    FOOTER_STACK_CARDS.forEach((card) => {
      leftoverOutro = pullBlock(bag, leftoverOutro, card);
    });
  }
  FOOTER_STACK_CARDS.forEach((card) => {
    if (bag[card]) {
      return;
    }
    source = pullBlock(bag, source, card);
  });
  source = stripEmptyWrapRows(source);
  if (bag.thankyou) {
    bag.thankyou = restyleThankYou(bag.thankyou);
  }
  if (bag.footer) {
    bag.footer = emailFooterBarHtml(pharmacyLogoImgFrom(bag.footer));
  }
  if (bag.footer || bag.branding) {
    bag.branding = poweredByBlockHtml();
  }
  const inner = FOOTER_STACK_CARDS.map((card) => bag[card] || '')
    .filter(Boolean)
    .join('') || unwrapOutroInner(leftoverOutro).trim();
  if (!inner) {
    return source;
  }
  const rebuilt = `<div class="mcc-email-outro" data-token="Email Footer">${inner}</div>`;
  const current = findWrapRange(source);
  if (!current) {
    return `${source}${rebuilt}`;
  }
  return (
    source.slice(0, current.end - 8) +
    footerWrapRowHtml(rebuilt) +
    source.slice(current.end - 8)
  );
};

const DEV_EMAIL_ICON_HOST_RE =
  /https:\/\/(?:s3\.amazonaws\.com\/rpx-dev-ext-images-qr|rpx-dev-ext-images-qr\.s3\.amazonaws\.com)\/email-icons\//gi;

export const rewriteEmailIconUrls = (html) =>
  String(html || '').replace(DEV_EMAIL_ICON_HOST_RE, `${EMAIL_ICON_BASE}/`);

export const LAYOUT_CARDS = [
  {
    id: 'header',
    label: 'Brand header',
    hint: 'Account branding logo from Accounts → Branding',
  },
  {
    id: 'hero',
    label: 'Hero + photo',
    hint: 'Headline, greeting, and locker image',
  },
  {
    id: 'text',
    label: 'Text card',
    hint: 'A branded paragraph you can edit',
  },
  {
    id: 'details',
    label: 'Pharmacy details',
    hint: 'Pharmacy, location, hours, and help',
  },
  {
    id: 'pickup_qr',
    label: 'Pickup code + QR',
    hint: 'Large code with a QR placeholder',
  },
  {
    id: 'columns',
    label: 'Two columns',
    hint: 'Side-by-side cells you can fill with anything',
  },
  {
    id: 'summary',
    label: 'Order summary',
    hint: 'Pickup / stock Rx table',
  },
  {
    id: 'payment',
    label: 'Pay Now button',
    hint: 'Adds the payment link under Payment Summary',
  },
  {
    id: 'pickup',
    label: 'Pickup code',
    hint: 'Large pickup code card',
  },
  {
    id: 'hours_grid',
    label: 'Hours grid',
    hint: 'Mon–Sun hours from Accounts / kiosk Hours of Operation',
  },
  {
    id: 'hours',
    label: 'Store hours',
    hint: 'Today’s hours of operation',
  },
  {
    id: 'badges',
    label: 'Trust badges',
    hint: 'Secure, 24/7, contactless, HIPAA',
  },
  {
    id: 'thankyou',
    label: 'Thank-you band',
    hint: 'Orange closing banner',
  },
  {
    id: 'image',
    label: 'Image card',
    hint: 'Drop in a logo or photo after inserting',
  },
  {
    id: 'footer',
    label: 'Email footer',
    hint: 'Pharmacy bar and locked iLocal Box Powered by line — stays inside the same email card',
  },
];

export const POWERED_BY_LOGO = DEFAULT_ACCOUNT_LOGO_URL;

const pharmacyLogoImgHtml = (url) =>
  `<img data-logo="pharmacy" data-card="brand-logo" src="${url}" alt="Pharmacy logo" width="110" height="36" style="display:inline-block;max-height:36px;max-width:110px;width:auto;height:auto;border:0;outline:none;background:#ffffff;border-radius:4px;padding:3px;" />`;

export const parsePharmacyLogoUrl = (html) => {
  const source = String(html || '');
  const match =
    source.match(/data-logo=["']pharmacy["'][^>]*\ssrc=["']([^"']+)["']/i) ||
    source.match(/\ssrc=["']([^"']+)["'][^>]*data-logo=["']pharmacy["']/i);
  return (match && match[1]) || '';
};

const EMAIL_FONT = 'font-family:Helvetica, Arial, sans-serif;';

const pharmacyLogoImgFrom = (html) => {
  const match = String(html || '').match(/<img\b[^>]*data-logo=["']pharmacy["'][^>]*>/i);
  return match ? match[0] : '';
};

const emailFooterBarHtml = (logoImg = '') => {
  const left = logoImg
    ? `<span data-logo-slot="pharmacy" contenteditable="false" style="display:inline-block;vertical-align:middle;">${logoImg}</span>`
    : `<span data-logo-slot="pharmacy" data-logo-hint="1" contenteditable="false" style="display:inline-block;vertical-align:middle;margin-right:10px;padding:6px 8px;border:1px dashed rgba(255,255,255,0.75);border-radius:6px;color:#ffffff;font-size:11px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;">Upload logo</span><span style="font-weight:700;font-size:15px;color:#ffffff;vertical-align:middle;">${tokenChip('Account Name')}</span>`;
  return `<table class="mcc-layout-card" data-card="footer" data-brand="footer" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#152C55;color:#ffffff;border-radius:0;margin:0;border-collapse:collapse;width:100%;"><tr><td valign="middle" style="padding:22px 24px;">${left}</td><td valign="middle" align="right" style="padding:22px 24px;text-align:right;color:#ffffff;"><p style="${EMAIL_FONT}font-size:13px;line-height:20px;color:#ffffff;text-align:right;margin:0 0 2px;">Questions? We're here to help.</p><p style="${EMAIL_FONT}font-size:13px;line-height:20px;color:#ffffff;text-align:right;margin:0;">${tokenChip('Location Phone')}&nbsp;&nbsp;|&nbsp;&nbsp;${tokenChip('Website')}</p></td></tr></table>`;
};

const thankYouTableHtml = (color, headline, subline) =>
  `<table class="mcc-layout-card" data-card="thankyou" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;margin:0;"><tr><td data-brand="cta" align="center" bgcolor="${color}" style="padding:22px 24px;background-color:${color};"><p style="${EMAIL_FONT}font-size:15px;line-height:22px;font-weight:normal;text-align:center;color:#ffffff;margin:0 0 4px;">${headline}</p><p style="${EMAIL_FONT}font-size:15px;line-height:22px;font-weight:normal;text-align:center;color:#ffffff;margin:0;">${subline}</p></td></tr></table>`;

const thankYouBandHtml = () =>
  thankYouTableHtml(
    '#E87722',
    `Thank you for choosing ${tokenChip('Account Name')}.`,
    'We make prescription pickup simple, secure, and convenient.'
  );

const innerOf = (html, tag) => {
  const out = [];
  const re = new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'gi');
  let m = re.exec(html);
  while (m) {
    out.push(m[1].trim());
    m = re.exec(html);
  }
  return out;
};

const restyleThankYou = (html) => {
  const source = String(html || '');
  const paras = innerOf(source, 'p');
  if (paras.length !== 2) {
    return source;
  }
  const color =
    (source.match(/bgcolor=["'](#[0-9a-f]{3,8})["']/i) || [])[1] ||
    (source.match(/background(?:-color)?:\s*(#[0-9a-f]{3,8})/i) || [])[1] ||
    '#E87722';
  return thankYouTableHtml(color, paras[0], paras[1]);
};

const DEFAULT_BADGES = [
  ['lock.png', 'Secure &amp; Private', 'Your medication is stored safely until you pick it up.'],
  ['time.png', 'Pick Up on Your Time', 'Open 24/7 for your convenience.'],
  ['contactless.png', 'Contactless', 'No waiting in line. No interaction needed.'],
  ['hipaa.png', 'HIPAA Compliant', 'Your health information is always protected.'],
].map(([file, title, body]) => [`${EMAIL_ICON_BASE}/${file}`, title, body]);

const badgesTableHtml = (items) =>
  `<table class="mcc-layout-card" data-card="badges" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;table-layout:fixed;"><tr>${items
    .map(
      ([src, title, body], i) =>
        `<td class="stack" width="25%" valign="top" align="center" style="width:25%;padding:4px 14px;${i ? 'border-left:1px solid #e2e8f0;' : ''}"><img src="${src}" alt="" width="40" height="40" style="display:block;width:40px;height:40px;margin:0 auto;border:0;outline:none;" /><p style="${EMAIL_FONT}font-size:14px;line-height:20px;font-weight:bold;text-align:center;color:#1e293b;margin:10px 0 6px;">${title}</p><p style="${EMAIL_FONT}font-size:12px;line-height:18px;font-weight:normal;text-align:center;color:#64748b;margin:0;">${body}</p></td>`
    )
    .join('')}</tr></table>`;

const restyleBadges = (html) => {
  const cells = innerOf(String(html || ''), 'td');
  if (!cells.length || cells.length > 6) {
    return html;
  }
  return badgesTableHtml(
    cells.map((cell, i) => {
      const img = cell.match(/<img\b[^>]*\ssrc=["']([^"']+)["']/i);
      const paras = innerOf(cell, 'p');
      const fallback = DEFAULT_BADGES[i] || DEFAULT_BADGES[0];
      return [
        (img && img[1]) || fallback[0],
        paras[0] || fallback[1],
        paras.slice(1).join('<br />') || fallback[2],
      ];
    })
  );
};

const FINE_PRINT = `${EMAIL_FONT}font-size:12px;line-height:18px;font-weight:normal;text-align:center;color:#98A2B3;`;

export const poweredByBlockHtml = () =>
  `<table class="mcc-layout-card mceNonEditable" data-card="branding" width="100%" cellpadding="0" cellspacing="0" border="0" contenteditable="false" style="border-collapse:collapse;width:100%;margin:0;background:transparent;"><tr><td valign="top" align="center" class="mceNonEditable" data-locked-widget="powered_by" contenteditable="false" style="padding:20px 24px 22px;text-align:center;"><p style="${FINE_PRINT}margin:0 0 2px;">This is an automated message, please do not reply.</p><p style="${FINE_PRINT}margin:0 0 8px;">&copy; ${new Date().getFullYear()} iLocal Box. All rights reserved.</p><p style="${EMAIL_FONT}font-size:11px;line-height:16px;font-weight:normal;text-align:center;color:#98A2B3;margin:0;"><em>Powered by</em> <img data-card="footer-logo" data-locked-widget="powered_by" src="${POWERED_BY_LOGO}" alt="iLocal" width="72" height="30" style="max-width:72px;margin:0 0 0 4px;padding:0;display:inline;vertical-align:middle;border:0;height:auto;outline:none;" /></p></td></tr></table>`;

const emailFooterHtml = () =>
  `<div class="mcc-email-outro" data-token="Email Footer">${thankYouBandHtml()}${emailFooterBarHtml()}${poweredByBlockHtml()}</div>`;

export const insertMissingFooterParts = (html) => {
  let source = String(html || '');
  if (
    !/mcc-email-outro/i.test(source) &&
    !/data-token=["']Email Footer["']/i.test(source)
  ) {
    return nestFooterInWrap(`${source}${emailFooterHtml()}`);
  }
  if (!/data-card=["']footer["']/i.test(source)) {
    source = source.replace(
      /(<div[^>]*(?:class="[^"]*mcc-email-outro|data-token=["']Email Footer["'])[^>]*>)/i,
      `$1${emailFooterBarHtml()}`
    );
  }
  if (!/data-card=["']thankyou["']/i.test(source)) {
    if (/data-card=["']footer["']/i.test(source)) {
      source = source.replace(
        /(<table[^>]*data-card=["']footer["'][^>]*>)/i,
        `${thankYouBandHtml()}$1`
      );
    }
  }
  if (!/data-locked-widget=["']powered_by["']/i.test(source)) {
    source = `${source}${poweredByBlockHtml()}`;
  }
  return nestFooterInWrap(source);
};

const restoreFooterExtras = (nextHtml, previousHtml) => {
  let next = String(nextHtml || '');
  const logo = parsePharmacyLogoUrl(previousHtml);
  if (logo) {
    next = next.replace(
      /<span[^>]*data-logo-slot=["']pharmacy["'][^>]*>[\s\S]*?<\/span>/i,
      `<span data-logo-slot="pharmacy" contenteditable="false" style="display:inline-block;vertical-align:middle;margin-right:10px;">${pharmacyLogoImgHtml(logo)}</span>`
    );
    next = next.replace(
      /(<td[^>]*data-logo-slot=["']pharmacy["'][^>]*>)[\s\S]*?(<\/td>)/i,
      `$1${pharmacyLogoImgHtml(logo)}$2`
    );
  }
  return next;
};

export const buildCardHtml = (type, colors = DEFAULT_BRAND_COLORS, logoUrl = '') => {
  const c = {
    ...DEFAULT_BRAND_COLORS,
    ...(colors || {}),
  };
  const cardStyle = `background:${c.card};color:${c.text};border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;margin:0 0 12px;`;
  switch (type) {
    case 'header':
      return `<table class="mcc-layout-card" data-card="header" data-brand="header" data-mcc-bleed="1" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;margin:0;">
<tr><td align="center" bgcolor="${c.header}" data-brand="header" style="padding:28px 24px;background-color:${c.header};text-align:center;">
${accountLogoImgHtml(logoUrl)}
</td></tr></table>`;
    case 'hero':
      return `<table class="mcc-layout-card" data-card="hero" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;margin:0 0 12px;">
<tr>
<td class="stack" width="48%" valign="middle" style="width:48%;padding:0 12px 0 0;">
<p style="margin:0 0 12px;font-size:28px;line-height:34px;font-weight:700;color:${c.text};">Your prescription is ready for pickup!</p>
<p style="margin:0 0 10px;font-size:16px;color:${c.text};">Hi ${tokenChip('First Name')},</p>
<p style="margin:0;font-size:16px;color:${c.text};">Write any message here. Move this block, change the photo, or replace the copy.</p>
</td>
<td class="stack" width="52%" valign="middle" align="right" style="width:52%;padding:0 0 0 12px;">
<img src="${EMAIL_ICON_BASE}/kiosk-locker-bag-v3.png" alt="" width="340" style="width:100%;max-width:340px;height:auto;border:0;display:block;border-radius:8px;" />
</td>
</tr>
</table>`;
    case 'text':
      return `<div class="mcc-layout-card" data-card="text" data-brand="card" style="${cardStyle}"><p data-brand="text" style="color:${c.text};margin:0;">Write your message here.</p></div>`;
    case 'details':
      return `<div class="mcc-layout-card" data-card="details" data-brand="card" style="${cardStyle}">
<p style="margin:0 0 6px;font-weight:700;color:${c.header};">Pharmacy</p>
<p style="margin:0 0 12px;color:${c.text};">${tokenChip('Account Name')}</p>
<p style="margin:0 0 6px;font-weight:700;color:${c.header};">Location</p>
<p style="margin:0 0 12px;color:${c.text};">${tokenChip('Location Name')}<br />${tokenChip('Location Address')}</p>
<p style="margin:0 0 6px;font-weight:700;color:${c.header};">Need Help?</p>
<p style="margin:0;color:${c.text};">Call us at ${tokenChip('Location Phone')}</p>
</div>`;
    case 'pickup_qr':
      return `<div class="mcc-layout-card" data-card="pickup_qr" data-brand="card" style="${cardStyle}text-align:center;">
<p style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1e3a5f;">Your Pickup Code</p>
<div data-pickup-code-chip="1" style="background-color:#F0F2F5;border-radius:8px;padding:12px 8px;margin:0 0 12px;">
<p style="margin:0;font-size:30px;line-height:34px;font-weight:700;letter-spacing:6px;color:#1e3a5f;">${tokenChip('Pickup Code')}</p>
</div>
<p style="margin:0;font-size:14px;line-height:20px;color:${c.text};">Scan this QR code at the kiosk or enter your pickup code.</p>
<div style="text-align:center;padding-top:14px;">${tokenChip('QR Code')}</div>
<p style="margin:12px 0 0;font-size:13px;color:#64748b;">Code expires in ${tokenChip('Expiry Date')}</p>
</div>`;
    case 'columns':
      return `<table class="mcc-layout-card" data-card="columns" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;margin:0 0 12px;">
<tr>
<td class="stack" width="50%" valign="top" style="width:50%;padding:12px;border:1px dashed #cbd5e1;background:${c.card};">
<p style="margin:0;color:${c.text};">Left column — type, paste, or drop an image here.</p>
</td>
<td class="stack" width="50%" valign="top" style="width:50%;padding:12px;border:1px dashed #cbd5e1;background:${c.card};">
<p style="margin:0;color:${c.text};">Right column — put a QR, photo, or any copy here.</p>
</td>
</tr>
</table>`;
    case 'summary':
      return `<div class="mcc-layout-card" data-card="summary" data-brand="card" data-hide-amount="1" data-summary-cols="drug_name,qty,prescriber" data-summary-mask="" style="${cardStyle}">${tokenChip('Order Summary')}</div>`;
    case 'payment':
    case 'cta':
      return payNowBlockHtml(c);
    case 'pickup':
      return `<div class="mcc-layout-card" data-card="pickup" data-brand="card" style="background:${c.card};color:${c.text};border:1px solid #e2e8f0;border-radius:10px;padding:12px 16px;margin:0;height:auto;"><p style="margin:0 0 4px;font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:#64748b;">Pickup code</p><p style="margin:0;font-size:22px;font-weight:700;letter-spacing:.08em;">${tokenChip('Pickup Code')}</p></div>`;
    case 'hours_grid':
      return `<table class="mcc-layout-card" data-card="hours" role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="width:100%;border-collapse:collapse;margin:0 0 12px;">
<tr>
<td width="50%" valign="top" style="width:50%;padding:0 8px 0 0;">
<div style="padding:16px;background:${c.card};border:1px solid #e2e8f0;">
<p style="margin:0 0 8px;font-weight:700;color:${c.text};">Pharmacy Hours</p>
${tokenChip('Pharmacy Hours')}
</div>
</td>
<td width="50%" valign="top" style="width:50%;padding:0 0 0 8px;">
<div style="padding:16px;background:${c.card};border:1px solid #e2e8f0;">
<p style="margin:0 0 8px;font-weight:700;color:${c.text};">Kiosk Hours</p>
${tokenChip('Kiosk Hours')}
</div>
</td>
</tr>
</table>`;
    case 'hours':
      return `<div class="mcc-layout-card" data-card="hours" data-brand="card" style="${cardStyle}"><p data-brand="text" style="color:${c.text};margin:0;">Open today from ${tokenChip('Hours')}</p></div>`;
    case 'badges':
      return badgesTableHtml(DEFAULT_BADGES);
    case 'thankyou':
      return thankYouBandHtml().replace(
        /background-color:#E87722;?/g,
        `background-color:${c.header};`
      ).replace(/bgcolor="#E87722"/g, `bgcolor="${c.header}"`);
    case 'image':
      return `<div class="mcc-layout-card" data-card="image" data-brand="card" style="${cardStyle}"><p data-brand="text" style="color:${c.text};margin:0;">Use the image button in the toolbar to add a logo or photo.</p></div>`;
    case 'footer':
      return emailFooterHtml();
    case 'stop':
      return `<div class="mcc-layout-card" data-card="stop" style="margin:12px 0 0;padding-top:12px;border-top:1px solid #e2e8f0;color:#64748b;font-size:12px;">${tokenChip('STOP')}</div>`;
    default:
      return '';
  }
};

const isLegacyEmailFooter = (html) => {
  const source = String(html || '');
  const isModernFooter =
    /data-locked-widget=["']powered_by["']/i.test(source) &&
    /data-card=["']footer["']/i.test(source);
  if (isModernFooter) {
    return false;
  }
  return (
    /This is an automated message/i.test(source) ||
    /data-lock-badge/i.test(source) ||
    /&#9742;/.test(source) ||
    /width="38%"/i.test(source) ||
    ((/data-token=["']Email Footer["']/i.test(source) ||
      /data-card=["']footer["']/i.test(source) ||
      /mcc-email-outro/i.test(source)) &&
      !/iLocal Box[.,] All Rights Reserved/i.test(source))
  );
};

const stripLegacyEmailFooter = (html) => {
  const source = String(html || '');
  const idx = source.search(
    /<div[^>]*(?:data-token=["']Email Footer["']|class="[^"]*mcc-email-outro|data-card=["']footer["'])/i
  );
  if (idx < 0) {
    return source;
  }
  return source.slice(0, idx);
};

export const stripEditorChrome = (html) => {
  let out = String(html || '');
  let prev = '';
  const block =
    /<(span|button)\b[^>]*class="[^"]*\bmcc-card-(?:tools|delete|handle|move)\b[^"]*"[^>]*>[\s\S]*?<\/\1>/gi;
  while (out !== prev) {
    prev = out;
    out = out.replace(block, '');
  }
  return out.replace(/>([^<]*)</g, (full, text) => {
    const cleaned = text
      .replace(/::/g, '')
      .replace(/×/g, '')
      .replace(/&times;/gi, '')
      .replace(/&#215;/g, '')
      .replace(/&#x0*d7;/gi, '');
    return `>${cleaned}<`;
  });
};

export const ensureEmailFooter = (html) => {
  const source = rewriteEmailIconUrls(stripEditorChrome(html))
    .replace(
      /<div[^>]*data-region=["']custom_note["'][^>]*>[\s\S]*?<\/div>/gi,
      ''
    )
    .replace(/<div[^>]*data-card=["']note["'][^>]*>\s*<\/div>/gi, '');
  if (!source.trim()) {
    return source;
  }
  if (isLegacyEmailFooter(source)) {
    return nestFooterInWrap(
      restoreFooterExtras(
        `${stripLegacyEmailFooter(source)}${buildCardHtml('footer')}`,
        source
      )
    );
  }
  if (/data-locked-widget=["']powered_by["']/i.test(source)) {
    return nestFooterInWrap(source);
  }
  if (/data-card=["']branding["']/i.test(source)) {
    return nestFooterInWrap(
      source.replace(
        /data-card=["']branding["']/i,
        'data-card="branding" data-locked-widget="powered_by"'
      )
    );
  }
  return nestFooterInWrap(`${source}${buildCardHtml('footer')}`);
};

const MONEY_SUMMARY_KEYS = { price: true, previous_price: true };

const removeInnermostTable = (source, needleRe) => {
  const marker = needleRe.exec(source);
  if (needleRe.global) {
    needleRe.lastIndex = 0;
  }
  if (!marker) return null;
  const before = source.slice(0, marker.index);
  const opens = [];
  const re = /<\/?table\b[^>]*>/gi;
  let match;
  while ((match = re.exec(before))) {
    if (match[0].charAt(1) === '/') opens.pop();
    else opens.push(match.index);
  }
  if (!opens.length) return null;
  const start = opens[opens.length - 1];
  const rest = source.slice(start);
  const tagRe = /<\/?table\b[^>]*>/gi;
  let depth = 0;
  let end = -1;
  let tag;
  while ((tag = tagRe.exec(rest))) {
    if (tag[0].charAt(1) === '/') {
      depth -= 1;
      if (depth === 0) {
        end = start + tag.index + tag[0].length;
        break;
      }
    } else {
      depth += 1;
    }
  }
  if (end < 0) return null;
  return source.slice(0, start) + source.slice(end);
};

export const stripPaymentContent = (html) => {
  let source = String(html || '');
  let guard = 0;
  while (guard < 6 && /Payment Summary/i.test(source)) {
    guard += 1;
    const next = removeInnermostTable(source, /Payment Summary/i);
    if (next == null || next === source) break;
    source = next;
  }
  source = source
    .replace(/<div\b[^>]*data-mcc-pay-now-wrap[^>]*>[\s\S]*?<\/div>/gi, '')
    .replace(/<p\b[^>]*data-mcc-pay-note[^>]*>[\s\S]*?<\/p>/gi, '')
    .replace(
      /<p\b[^>]*>\s*Save time and pay online before you arrive\.?\s*<\/p>/gi,
      ''
    )
    .replace(/<p\b[^>]*>\s*Pay at the kiosk with a card\.?\s*<\/p>/gi, '')
    .replace(/<a\b[^>]*data-mcc-pay-now[^>]*>[\s\S]*?<\/a>/gi, '')
    .replace(/<a\b[^>]*>\s*Pay Now\s*<\/a>/gi, '')
    .replace(/<table\b[^>]*data-mcc-total-due[^>]*>[\s\S]*?<\/table>/gi, '')
    .replace(/<div\b[^>]*data-card=["']payment["'][^>]*>[\s\S]*?<\/div>/gi, '')
    .replace(
      /<table\b[^>]*data-card=["']payment["'][^>]*>[\s\S]*?<\/table>/gi,
      ''
    )
    .replace(/<span\b[^>]*data-token=["']Amount["'][^>]*>[\s\S]*?<\/span>/gi, '')
    .replace(/\{\{\s*Amount\s*\}\}/g, '')
    .replace(/data-summary-cols="([^"]*)"/gi, (_full, cols) => {
      const next = String(cols)
        .split(',')
        .map((item) => item.trim())
        .filter((key) => key && !MONEY_SUMMARY_KEYS[key]);
      return `data-summary-cols="${next.join(',')}"`;
    })
    .replace(/data-hide-amount=["'][^"']*["']/gi, 'data-hide-amount="1"');
  if (/data-card=["']summary["']|data-summary-cols=/i.test(source)) {
    return setHideAmountDue(source, true);
  }
  return source;
};

export {
  htmlHasOrderSummary,
  htmlHidesAmountDue,
  parseSummaryConfig,
  setHideAmountDue,
  setSummaryConfig,
} from './orderSummary';

const CARD_LABELS = {
  header: 'Brand header',
  hero: 'Hero + photo',
  text: 'Text card',
  details: 'Pharmacy details',
  pickup_qr: 'Pickup code + QR',
  columns: 'Two columns',
  summary: 'Order summary',
  payment: 'Pay Now button',
  pickup: 'Pickup code',
  hours_grid: 'Hours grid',
  hours: 'Store hours',
  cta: 'Pay Now button',
  badges: 'Trust badges',
  thankyou: 'Thank-you band',
  image: 'Image card',
  footer: 'Email footer',
  stop: 'STOP',
  note: 'Custom note',
};

export const isLockedLayoutCard = (node) => {
  if (!node || node.nodeType !== 1 || !node.getAttribute) {
    return true;
  }
  const card = node.getAttribute('data-card');
  return (
    node.getAttribute('data-locked-widget') === 'powered_by' ||
    card === 'branding' ||
    card === 'footer-logo' ||
    card === 'brand-logo'
  );
};

export const collectRemovableLayoutCardNodes = (root) => {
  if (!root || !root.querySelectorAll) {
    return [];
  }
  const seen = [];
  return Array.prototype.slice
    .call(
      root.querySelectorAll(
        '.mcc-layout-card, div[data-card], table[data-card]'
      )
    )
    .filter((node) => {
      if (seen.indexOf(node) !== -1) {
        return false;
      }
      seen.push(node);
      if (isLockedLayoutCard(node)) {
        return false;
      }
      let parent = node.parentNode;
      while (parent && parent !== root) {
        if (
          parent.classList &&
          parent.classList.contains('mcc-layout-card')
        ) {
          return false;
        }
        if (parent.getAttribute && parent.getAttribute('data-card')) {
          const tag = String(parent.tagName || '').toLowerCase();
          if (tag === 'div' || tag === 'table') {
            return false;
          }
        }
        parent = parent.parentNode;
      }
      return true;
    });
};

const parseLayoutRoot = (html) => {
  if (typeof DOMParser === 'undefined') {
    return null;
  }
  const doc = new DOMParser().parseFromString(
    `<div id="mcc-root">${html || ''}</div>`,
    'text/html'
  );
  return doc.getElementById('mcc-root');
};

export const removableLayoutCards = (html) => {
  const root = parseLayoutRoot(html);
  if (!root) {
    return [];
  }
  return collectRemovableLayoutCardNodes(root);
};

export const listRemovableLayoutCards = (html) =>
  removableLayoutCards(html).map((node, index) => {
    const id = node.getAttribute('data-card') || '';
    const match = LAYOUT_CARDS.filter((card) => card.id === id)[0];
    return {
      index,
      id,
      label: (match && match.label) || CARD_LABELS[id] || id || 'Section',
    };
  });

export const removeLayoutCardAt = (html, index) => {
  const root = parseLayoutRoot(html);
  if (!root) {
    return html || '';
  }
  const cards = collectRemovableLayoutCardNodes(root);
  const node = cards[index];
  if (!node || !node.parentNode) {
    return html || '';
  }
  const parent = node.parentNode;
  parent.removeChild(node);
  if (
    parent.className &&
    String(parent.className).indexOf('mcc-email-outro') !== -1 &&
    !parent.querySelector(
      '[data-locked-widget="powered_by"], [data-card], table, img, p, div'
    ) &&
    parent.parentNode
  ) {
    parent.parentNode.removeChild(parent);
  }
  return root.innerHTML;
};

export const setPharmacyLogo = (html, url) => {
  const safeUrl = String(url || '').replace(/"/g, '');
  if (!safeUrl) {
    return String(html || '');
  }
  let source = String(html || '');
  if (!/data-logo-slot=["']pharmacy["']/i.test(source)) {
    source = ensureEmailFooter(source);
  }
  const img = pharmacyLogoImgHtml(safeUrl);
  if (/data-logo=["']pharmacy["']/i.test(source)) {
    return source.replace(/<img\b[^>]*data-logo=["']pharmacy["'][^>]*>/i, img);
  }
  if (/data-logo-slot=["']pharmacy["']/i.test(source)) {
    return source.replace(
      /(<td[^>]*data-logo-slot=["']pharmacy["'][^>]*>)[\s\S]*?(<\/td>)/i,
      `$1${img}$2`
    );
  }
  return source;
};



// WEBPACK FOOTER //
// ./src/components/message-control-center/layoutCards.js